import { MODULES, PERMISSIONS } from '@ibe/constants';
import {
  Button,
  Field,
  FieldCheckbox,
  FieldInput,
  FieldLabel,
  FieldSelect,
  FieldTextarea,
} from '@ibe/ui';
import { useEffect, useState, type ReactNode } from 'react';
import {
  Controller,
  useWatch,
  type Control,
  type FieldErrors,
  type UseFormRegister,
  type UseFormSetValue,
} from 'react-hook-form';
import { Plus } from 'lucide-react';

import { ModuleGuard, PermissionGuard } from '../../../auth/permission-guard.js';
import { LogoUpload } from '../../../components/media/logo-upload.js';
import { useApiList } from '../../../lib/api/hooks.js';
import { dialCodeOptions } from '../../../lib/forms/dial-codes.js';
import { TIMEZONE_OPTIONS } from '../../../lib/forms/timezones.js';
import { pmsCredentials, pmsCredentialRow } from '../../../lib/api/pms-credentials.js';
import { CreatePmsCredentialDialog } from './property-pms-credential-dialog.js';
import { useCurrencies } from '../../../lib/reference/currencies.js';
import { useCities, useCountries, useStates } from '../api/queries.js';
import {
  PROPERTY_STATUS_OPTIONS,
  PROPERTY_TYPE_OPTIONS,
  STAR_RATING_OPTIONS,
  type PropertyFormInput,
} from '../api/schemas.js';

/**
 * A property's inputs, independent of what surrounds them.
 *
 * Separated from the pages because create and edit are the same thirty-one
 * fields in two containers, and a field set that lives inside one of them gets
 * copied the moment the other appears — after which they drift: a placeholder
 * fixed in one, a `maxLength` added to the other.
 *
 * It takes the four pieces it needs rather than the whole form object, so it
 * cannot reach for `submit` or `reset` and quietly become the thing that owns
 * submission. The container owns that.
 *
 * The fields are grouped under headings because thirty-one inputs in one
 * column is a wall. The grouping is also the reading order an operator works
 * in: what the property is, then how it runs, then how to reach it, then where
 * it is.
 */

export interface PropertyFieldsProps {
  readonly register: UseFormRegister<PropertyFormInput>;
  readonly errors: FieldErrors<PropertyFormInput>;
  /**
   * Needed by `Controller` for the select controls.
   *
   * `register` only works on controls the browser knows about. A `FieldSelect`
   * is a button and a portalled listbox, so its value has to be bridged into
   * the form explicitly.
   */
  readonly control: Control<PropertyFormInput>;
  /** Clears the location fields below the one that changed. */
  readonly setValue: UseFormSetValue<PropertyFormInput>;
  /**
   * Which screen this is.
   *
   * Two fields differ, and both differences are the service's: a code cannot
   * be changed after issue, and a status cannot be chosen before the property
   * exists.
   */
  readonly mode: 'create' | 'edit';
  /** The property identifier when editing, so the logo is uploaded in scope. */
  readonly propertyId?: string;
}

function Section({
  title,
  description,
  children,
}: {
  readonly title: string;
  readonly description?: string;
  readonly children: ReactNode;
}): React.JSX.Element {
  return (
    // A fieldset and legend rather than a heading and a div: this is a group
    // of controls, and the legend is what names the group for a screen reader
    // moving between them.
    <fieldset className="space-y-4 border-t border-border pt-4 first:border-t-0 first:pt-0">
      <legend className="sr-only">{title}</legend>
      <div className="space-y-1">
        <h2 className="text-body font-semibold">{title}</h2>
        {description === undefined ? null : (
          <p className="text-caption text-muted-foreground">{description}</p>
        )}
      </div>
      {children}
    </fieldset>
  );
}

function Row({ children }: { readonly children: ReactNode }): React.JSX.Element {
  return <div className="grid grid-cols-1 gap-4 md:grid-cols-2">{children}</div>;
}

function Triple({ children }: { readonly children: ReactNode }): React.JSX.Element {
  return <div className="grid grid-cols-1 gap-4 md:grid-cols-3">{children}</div>;
}

const SEARCH_DEBOUNCE_MS = 300;

function useDebouncedSearchTerm(value: string, delay = SEARCH_DEBOUNCE_MS): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounced(value);
    }, delay);
    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);
  return debounced;
}

export function PropertyFields({
  register,
  errors,
  control,
  setValue,
  mode,
  propertyId,
}: PropertyFieldsProps): React.JSX.Element {
  // The field name is a second type argument, not just a string. Without it
  // `useWatch` widens to the union of every field's type and the cascade below
  // stops type-checking against the query hooks, which take a plain code.
  // `control` is passed explicitly rather than read from context: the form
  // pages render these fields without a `FormProvider`, and a context-less
  // `useWatch` throws on first render rather than degrading.
  const countryCode = useWatch<PropertyFormInput, 'countryCode'>({ control, name: 'countryCode' });
  const stateCode = useWatch<PropertyFormInput, 'stateCode'>({ control, name: 'stateCode' });
  const pmsConfigured = useWatch<PropertyFormInput, 'pmsConfigured'>({
    control,
    name: 'pmsConfigured',
  });

  const [countrySearch, setCountrySearch] = useState('');
  const [stateSearch, setStateSearch] = useState('');
  const [citySearch, setCitySearch] = useState('');

  const debouncedCountrySearch = useDebouncedSearchTerm(countrySearch);
  const debouncedStateSearch = useDebouncedSearchTerm(stateSearch);
  const debouncedCitySearch = useDebouncedSearchTerm(citySearch);

  const currenciesQuery = useCurrencies();
  const countriesQuery = useCountries(debouncedCountrySearch);
  const statesQuery = useStates(countryCode, debouncedStateSearch);
  const citiesQuery = useCities(countryCode, stateCode, debouncedCitySearch);

  // No entry carries the empty string: "nothing chosen" is the placeholder,
  // and an option whose value is empty cannot be told apart from one.
  const currencyOptions = (currenciesQuery.data ?? []).map((currency) => ({
    value: currency.code,
    label: `${currency.code} — ${currency.name}`,
  }));

  const countryOptions = (countriesQuery.data ?? []).map((country) => ({
    value: country.code,
    label: country.name,
  }));

  const stateOptions = (statesQuery.data ?? []).map((state) => ({
    value: state.code,
    label: state.name,
  }));

  const cityOptions = (citiesQuery.data ?? []).map((city) => ({
    value: city.code,
    label: city.name,
  }));

  const [pmsCredentialAdditions, setPmsCredentialAdditions] = useState<
    readonly { value: string; label: string }[]
  >([]);

  const pmsCredentialsQuery = useApiList({
    resource: 'pmsCredentials',
    path: pmsCredentials.endpoints.list,
    schema: pmsCredentialRow,
    params: { page: 1, pageSize: 100, scope: { status: 'ACTIVE' } },
  });

  const pmsCredentialOptions = [
    ...pmsCredentialAdditions,
    ...(pmsCredentialsQuery.data?.items ?? [])
      .filter((c) => c.status === 'ACTIVE')
      .filter((c) => !pmsCredentialAdditions.some((a) => a.value === c.id))
      .map((c) => ({ value: c.id, label: c.name ?? '' })),
  ];

  const gatewayPlaceholderOptions = [{ value: '', label: 'Not set' }] as const;

  return (
    <div className="space-y-6">
      <Section
        title="Identity"
        description="How this property is named, and how it is referred to in every booking made against it."
      >
        <Row>
          <Field error={errors.name?.message} required>
            <FieldLabel>Property name</FieldLabel>
            <FieldInput autoComplete="off" {...register('name')} />
          </Field>

          <Field
            error={errors.code?.message}
            required
            description={
              mode === 'edit'
                ? 'Fixed once the property exists — it appears in every booking reference already issued.'
                : 'Short, stable and yours. It appears in every booking reference.'
            }
          >
            <FieldLabel>Property code</FieldLabel>
            <FieldInput
              autoComplete="off"
              placeholder="GRAND-01"
              readOnly={mode === 'edit'}
              className={mode === 'edit' ? 'bg-muted' : undefined}
              {...register('code')}
            />
          </Field>
        </Row>

        <Row>
          <Field
            error={errors.displayName?.message}
            description="Shown to guests when it should differ from the legal name."
          >
            <FieldLabel>Display name</FieldLabel>
            <FieldInput autoComplete="off" {...register('displayName')} />
          </Field>

          <Field
            error={errors.propertyType?.message}
            description="Choose the kind of property this is."
          >
            <FieldLabel>Property type</FieldLabel>
            <Controller
              control={control}
              name="propertyType"
              render={({ field }) => (
                <FieldSelect
                  value={field.value}
                  onValueChange={field.onChange}
                  onBlur={field.onBlur}
                  options={PROPERTY_TYPE_OPTIONS}
                  placeholder="Select a property type"
                  searchable
                />
              )}
            />
          </Field>
        </Row>

        <Row>
          <Field error={errors.starRating?.message}>
            <FieldLabel>Star rating</FieldLabel>
            <Controller
              control={control}
              name="starRating"
              render={({ field }) => (
                <FieldSelect
                  value={field.value}
                  onValueChange={field.onChange}
                  onBlur={field.onBlur}
                  options={STAR_RATING_OPTIONS}
                  placeholder="Not rated"
                  searchable
                />
              )}
            />
          </Field>

          {mode === 'create' ? null : (
            <Field
              error={errors.status?.message}
              required
              description="Only an ACTIVE property can be sold."
            >
              <FieldLabel>Status</FieldLabel>
              <Controller
                control={control}
                name="status"
                render={({ field }) => (
                  <FieldSelect
                    value={field.value}
                    onValueChange={field.onChange}
                    onBlur={field.onBlur}
                    options={PROPERTY_STATUS_OPTIONS}
                    placeholder="Select a status"
                    searchable
                  />
                )}
              />
            </Field>
          )}
        </Row>

        <Field error={errors.description?.message}>
          <FieldLabel>Description</FieldLabel>
          <FieldTextarea {...register('description')} />
        </Field>
      </Section>

      <Section
        title="Operations"
        description="The property's own clock and currency. Every rate, arrival and report is read against these."
      >
        <Row>
          <Field
            error={errors.timezone?.message}
            required
            description="A check-in time means nothing without it."
          >
            <FieldLabel>Time zone</FieldLabel>
            <Controller
              control={control}
              name="timezone"
              render={({ field }) => (
                <FieldSelect
                  value={field.value}
                  onValueChange={field.onChange}
                  onBlur={field.onBlur}
                  options={TIMEZONE_OPTIONS}
                  placeholder="Select a time zone"
                  searchable
                />
              )}
            />
          </Field>

          <Field error={errors.currency?.message} required>
            <FieldLabel>Currency</FieldLabel>
            <Controller
              control={control}
              name="currency"
              render={({ field }) => (
                <FieldSelect
                  value={field.value}
                  onValueChange={field.onChange}
                  onBlur={field.onBlur}
                  options={currencyOptions}
                  placeholder="Select a currency"
                  searchable
                />
              )}
            />
          </Field>
        </Row>

        <Row>
          <Field
            error={errors.defaultCheckInTime?.message}
            description="Local to the property. Defaults to 14:00."
          >
            <FieldLabel>Check-in time</FieldLabel>
            <FieldInput type="time" step="60" {...register('defaultCheckInTime')} />
          </Field>

          <Field
            error={errors.defaultCheckOutTime?.message}
            description="Local to the property. Defaults to 10:00."
          >
            <FieldLabel>Check-out time</FieldLabel>
            <FieldInput type="time" step="60" {...register('defaultCheckOutTime')} />
          </Field>
        </Row>
      </Section>

      <Section title="Contact" description="How a guest, and this platform, reach the property.">
        <Row>
          <Field error={errors.email?.message}>
            <FieldLabel>Email</FieldLabel>
            <FieldInput autoComplete="off" type="email" {...register('email')} />
          </Field>

          <Field error={errors.website?.message}>
            <FieldLabel>Website</FieldLabel>
            <FieldInput autoComplete="off" placeholder="https://" {...register('website')} />
          </Field>
        </Row>

        <Triple>
          <Field error={errors.phoneCode?.message}>
            <FieldLabel>Dialling code</FieldLabel>
            <Controller
              control={control}
              name="phoneCode"
              render={({ field }) => (
                <FieldSelect
                  value={field.value}
                  onValueChange={field.onChange}
                  onBlur={field.onBlur}
                  options={dialCodeOptions(countriesQuery.data ?? [], field.value)}
                  placeholder="Select a dialling code"
                  searchable
                />
              )}
            />
          </Field>

          <Field error={errors.phone?.message}>
            <FieldLabel>Phone</FieldLabel>
            <FieldInput autoComplete="off" {...register('phone')} />
          </Field>

          <Field error={errors.mobile?.message}>
            <FieldLabel>Mobile</FieldLabel>
            <FieldInput autoComplete="off" {...register('mobile')} />
          </Field>
        </Triple>

        <Field
          error={errors.abn?.message}
          description="Or the equivalent registration number where the property trades."
        >
          <FieldLabel>Business number</FieldLabel>
          <FieldInput autoComplete="off" {...register('abn')} />
        </Field>
      </Section>

      <Section
        title="Location"
        description="Country, then state, then city: each list is drawn from the one above it."
      >
        <Triple>
          <Field error={errors.countryCode?.message}>
            <FieldLabel>Country</FieldLabel>
            <Controller
              control={control}
              name="countryCode"
              render={({ field }) => (
                <FieldSelect
                  value={field.value}
                  onValueChange={(value) => {
                    field.onChange(value);
                    // Cleared, not left behind. A state code is unique within
                    // its country and nowhere else, so keeping the old one
                    // after the country changes produces a request the
                    // service rejects — and the operator cannot see why.
                    setValue('stateCode', '');
                    setValue('cityCode', '');
                    setStateSearch('');
                    setCitySearch('');
                  }}
                  onSearchChange={setCountrySearch}
                  onBlur={field.onBlur}
                  options={countryOptions}
                  placeholder="Select a country"
                />
              )}
            />
          </Field>

          <Field error={errors.stateCode?.message}>
            <FieldLabel>State / Province</FieldLabel>
            <Controller
              control={control}
              name="stateCode"
              render={({ field }) => (
                <FieldSelect
                  value={field.value}
                  onValueChange={(value) => {
                    field.onChange(value);
                    setValue('cityCode', '');
                    setCitySearch('');
                  }}
                  onSearchChange={setStateSearch}
                  onBlur={field.onBlur}
                  options={stateOptions}
                  disabled={countryCode === ''}
                  placeholder={
                    countryCode === ''
                      ? 'Select a country first'
                      : stateOptions.length === 0
                        ? 'No states recorded'
                        : 'Select a state'
                  }
                />
              )}
            />
          </Field>

          <Field error={errors.cityCode?.message}>
            <FieldLabel>City</FieldLabel>
            <Controller
              control={control}
              name="cityCode"
              render={({ field }) => (
                <FieldSelect
                  value={field.value}
                  onValueChange={field.onChange}
                  onSearchChange={setCitySearch}
                  onBlur={field.onBlur}
                  options={cityOptions}
                  disabled={stateCode === ''}
                  placeholder={
                    stateCode === ''
                      ? 'Select a state first'
                      : cityOptions.length === 0
                        ? 'No cities recorded'
                        : 'Select a city'
                  }
                  searchable
                />
              )}
            />
          </Field>
        </Triple>

        <Row>
          <Field error={errors.addressLine1?.message}>
            <FieldLabel>Address line 1</FieldLabel>
            <FieldInput autoComplete="off" {...register('addressLine1')} />
          </Field>

          <Field error={errors.addressLine2?.message}>
            <FieldLabel>Address line 2</FieldLabel>
            <FieldInput autoComplete="off" {...register('addressLine2')} />
          </Field>
        </Row>

        <Triple>
          <Field error={errors.addressLine3?.message}>
            <FieldLabel>Address line 3</FieldLabel>
            <FieldInput autoComplete="off" {...register('addressLine3')} />
          </Field>

          <Field error={errors.addressLine4?.message}>
            <FieldLabel>Address line 4</FieldLabel>
            <FieldInput autoComplete="off" {...register('addressLine4')} />
          </Field>

          <Field error={errors.postCode?.message}>
            <FieldLabel>Postcode</FieldLabel>
            <FieldInput autoComplete="off" {...register('postCode')} />
          </Field>
        </Triple>

        <Row>
          <Field
            error={errors.latitude?.message}
            required
            description="Decimal degrees. Melbourne is -37.8136."
          >
            <FieldLabel>Latitude</FieldLabel>
            <FieldInput autoComplete="off" inputMode="decimal" {...register('latitude')} />
          </Field>

          <Field
            error={errors.longitude?.message}
            required
            description="Decimal degrees. Melbourne is 144.9631."
          >
            <FieldLabel>Longitude</FieldLabel>
            <FieldInput autoComplete="off" inputMode="decimal" {...register('longitude')} />
          </Field>
        </Row>
      </Section>

      <Section title="Branding" description="The logo shown wherever this property is presented.">
        <Row>
          <Field error={errors.logo?.message}>
            <FieldLabel>Logo</FieldLabel>
            <Controller
              control={control}
              name="logo"
              render={({ field }) => (
                <LogoUpload
                  key={field.value}
                  value={field.value}
                  onChange={field.onChange}
                  propertyId={propertyId}
                  disabled={field.disabled}
                  error={errors.logo?.message}
                />
              )}
            />
          </Field>

          <Field
            error={errors.logoAltText?.message}
            // Not optional in spirit: an image with no alt text fails WCAG
            // 1.1.1, and the service falls back to the property's name rather
            // than storing an empty one.
            description="Describes the logo for anyone who cannot see it. Defaults to the property name."
          >
            <FieldLabel>Logo alt text</FieldLabel>
            <FieldInput autoComplete="off" {...register('logoAltText')} />
          </Field>
        </Row>
      </Section>

      {/* Absent entirely for a tenant that has not bought the PMS module.
          Two fields that can only ever stay empty are worse than no fields:
          they ask a question — "which PMS?" — that the operator has no way to
          answer, and they make a property look unsynchronised rather than
          unsynchronisable. */}
      <ModuleGuard module={MODULES.PMS}>
        <Section
          title="Property management system"
          description="Set these only when this property is synchronised with a PMS."
        >
          <PermissionGuard permission={PERMISSIONS.PMS_CONFIGURE}>
            <CreatePmsCredentialDialog
              onCreated={(credential) => {
                setPmsCredentialAdditions((prev) => [
                  ...prev,
                  { value: credential.id, label: credential.name },
                ]);
                setValue('pmsAdapterId', credential.id);
              }}
            >
              <Button type="button" size="sm">
                <Plus className="h-4 w-4" aria-hidden="true" />
                Create a new PMS credential
              </Button>
            </CreatePmsCredentialDialog>
          </PermissionGuard>

          <Row>
            <Field error={errors.pmsConfigured?.message}>
              <FieldLabel>PMS configured</FieldLabel>
              <Controller
                control={control}
                name="pmsConfigured"
                render={({ field }) => (
                  <FieldCheckbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    onBlur={field.onBlur}
                  >
                    Enabled
                  </FieldCheckbox>
                )}
              />
            </Field>

            {pmsConfigured ? (
              <Field error={errors.pmsAdapterId?.message}>
                <FieldLabel>PMS provider</FieldLabel>
                <Controller
                  control={control}
                  name="pmsAdapterId"
                  render={({ field }) => (
                    <FieldSelect
                      value={field.value}
                      onValueChange={field.onChange}
                      onBlur={field.onBlur}
                      options={[...gatewayPlaceholderOptions, ...pmsCredentialOptions]}
                      placeholder="Select a PMS credential"
                      searchable
                    />
                  )}
                />
              </Field>
            ) : null}

            {pmsConfigured ? (
              <Field error={errors.pmsPropertyId?.message}>
                <FieldLabel>PMS property id</FieldLabel>
                <FieldInput autoComplete="off" {...register('pmsPropertyId')} />
              </Field>
            ) : null}
          </Row>
        </Section>
      </ModuleGuard>
    </div>
  );
}
